import {useState, useEffect} from 'react';
import axios from 'axios';
import {useFormik} from 'formik';
import * as Yup from 'yup';
import {CrudAvatarUpload} from '@/partials/crud';
import {enqueueSnackbar} from 'notistack';
import {useUser} from "@/contexts/UserContext.tsx"; // For notifications if needed

interface IGeneralSettingsProps {
    title: string;
}

const EU_COUNTRIES = [
    'Austria', 'Belgium', 'Bulgaria', 'Croatia', 'Cyprus',
    'Czech Republic', 'Denmark', 'Estonia', 'Finland', 'France',
    'Germany', 'Greece', 'Hungary', 'Ireland', 'Italy', 'Latvia',
    'Lithuania', 'Luxembourg', 'Malta', 'Netherlands', 'Poland',
    'Portugal', 'Romania', 'Slovakia', 'Slovenia', 'Spain',
    'Sweden', 'Not EU'
];

const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    country: ''
};

const settingsSchema = Yup.object().shape({
    firstName: Yup.string()
        .min(2, 'Minimum 2 characters')
        .max(50, 'Maximum 50 characters')
        .required('First name is required'),
    lastName: Yup.string()
        .min(2, 'Minimum 2 characters')
        .max(50, 'Maximum 50 characters')
        .required('Last name is required'),
    email: Yup.string()
        .email('Wrong email format')
        .min(3, 'Minimum 3 symbols')
        .max(50, 'Maximum 50 symbols')
        .required('Email is required'),
    company: Yup.string()
        .min(2, 'Minimum 2 characters')
        .max(100, 'Maximum 100 characters')
        .required('Company name is required'),
    country: Yup.string().required('Country selection is required')
});

const BasicSettings = ({title}: IGeneralSettingsProps) => {
    const [loading, setLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);

    const API_URL = import.meta.env.VITE_APP_API_URL; // Assuming you set this in env
    const API_USER = `${API_URL}/user`;
    const {refreshUser} = useUser();

    const formik = useFormik({
        initialValues,
        validationSchema: settingsSchema,
        onSubmit: async (values, {setStatus, setSubmitting}) => {
            setSubmitLoading(true);
            try {
                // Send PUT request to update user settings
                const response = await axios.put(API_USER, {
                    first_name: values.firstName,
                    last_name: values.lastName,
                    email: values.email,
                    company: values.company,
                    country: values.country
                });
                console.log('Update response:', response.data);
                await refreshUser();
                // Optionally show a snackbar notification
                enqueueSnackbar('General Settings updated successfully.', {variant: 'success'});
            } catch (error: any) {
                const errorMessage = error.response?.data?.detail || 'An unexpected error occurred.';
                console.error(errorMessage);
                setStatus(errorMessage);
                enqueueSnackbar(errorMessage, {variant: 'error'});
            } finally {
                setSubmitting(false);
                setSubmitLoading(false);
            }
        }
    });

    // Fetch current user data on mount
    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const response = await axios.get(API_USER, {
                    headers: {
                        Accept: 'application/json'
                    }
                });
                const userData = response.data;
                // Populate form fields with fetched user data
                formik.setValues({
                    firstName: userData.first_name || '',
                    lastName: userData.last_name || '',
                    email: userData.email || '',
                    company: userData.company || '',
                    country: userData.country || ''
                });
            } catch (error: any) {
                const errorMessage = error.response?.data?.detail || 'Failed to fetch user data.';
                console.error(errorMessage);
                enqueueSnackbar(errorMessage, {variant: 'error'});
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) return <div>Loading user settings...</div>;

    return (
        <div className="card pb-2.5">
            <form onSubmit={formik.handleSubmit}>
                <div className="card-header" id="general_settings">
                    <h3 className="card-title">{title}</h3>
                </div>
                <div className="card-body grid gap-5">
                    {/* Photo Upload Section */}
                    {/*<div className="flex items-center flex-wrap lg:flex-nowrap gap-2.5">
              <label className="form-label max-w-56">Photo</label>
              <div className="flex items-center justify-between flex-wrap grow gap-2.5">
                <span className="text-2sm font-medium text-gray-600">150x150px JPEG, PNG Image</span>
                <CrudAvatarUpload />
              </div>
            </div>*/}

                    {/* First Name */}
                    <div className="grid gap-1">
                        <label className="form-label">First Name</label>
                        <input
                            type="text"
                            className="input"
                            placeholder="Enter First Name"
                            {...formik.getFieldProps('firstName')}
                        />
                        {formik.touched.firstName && formik.errors.firstName && (
                            <span role="alert" className="text-danger text-xs">
                {formik.errors.firstName}
              </span>
                        )}
                    </div>

                    {/* Last Name */}
                    <div className="grid gap-1">
                        <label className="form-label">Last Name</label>
                        <input
                            type="text"
                            className="input"
                            placeholder="Enter Last Name"
                            {...formik.getFieldProps('lastName')}
                        />
                        {formik.touched.lastName && formik.errors.lastName && (
                            <span role="alert" className="text-danger text-xs">
                {formik.errors.lastName}
              </span>
                        )}
                    </div>

                    {/* Email */}
                    <div className="grid gap-1">
                        <label className="form-label">Email</label>
                        <input
                            type="email"
                            className="input"
                            placeholder="email@example.com"
                            {...formik.getFieldProps('email')}
                        />
                        {formik.touched.email && formik.errors.email && (
                            <span role="alert" className="text-danger text-xs">
                {formik.errors.email}
              </span>
                        )}
                    </div>

                    {/* Company Name */}
                    <div className="grid gap-1">
                        <label className="form-label">Company Name</label>
                        <input
                            type="text"
                            className="input"
                            placeholder="Your Company"
                            {...formik.getFieldProps('company')}
                        />
                        {formik.touched.company && formik.errors.company && (
                            <span role="alert" className="text-danger text-xs">
                {formik.errors.company}
              </span>
                        )}
                    </div>

                    {/* Country */}
                    <div className="grid gap-1">
                        <label className="form-label">Country</label>
                        <select
                            className="select"
                            {...formik.getFieldProps('country')}
                        >
                            <option value="" disabled>Select your country</option>
                            {EU_COUNTRIES.map((countryOption) => (
                                <option key={countryOption} value={countryOption}>{countryOption}</option>
                            ))}
                        </select>
                        {formik.touched.country && formik.errors.country && (
                            <span role="alert" className="text-danger text-xs">
                {formik.errors.country}
              </span>
                        )}
                    </div>

                    <div className="flex justify-end">
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={submitLoading || formik.isSubmitting}
                        >
                            {submitLoading ? 'Please wait' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export {BasicSettings, type IGeneralSettingsProps};
